import React, { useEffect, useState, useImperativeHandle, forwardRef, useMemo } from 'react';
import { Card, Tree, Spin, Button, Space, Empty } from 'antd';
import type { TreeProps, DataNode } from 'antd/es/tree';
import { ReloadOutlined } from '@ant-design/icons';

export type TreeListRef = {
  reload: () => Promise<void>;
};

export interface TreeListProps<T> {
  title?: React.ReactNode;
  fetcher: () => Promise<T[]>;
  renderTitle?: (item: T) => React.ReactNode;
  rowKey?: (item: T) => string | number;
  childrenKey?: string;
  extra?: React.ReactNode;
  cardProps?: any;
  treeProps?: Omit<TreeProps, 'treeData'>;
  showReload?: boolean;
}

const TreeList = forwardRef(<T extends Record<string, any>>(props: TreeListProps<T>, ref: React.Ref<TreeListRef>) => {
  const {
    title,
    fetcher,
    renderTitle,
    rowKey = (item: T) => item.id,
    childrenKey = 'children',
    extra,
    cardProps,
    treeProps,
    showReload = true
  } = props;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const doFetch = async () => {
    setLoading(true);
    try {
      const items = await fetcher();
      setData(items || []);
      
      // Auto expand all by default or let user handle? 
      // Departments.tsx expands all. Let's try to collect all keys.
      if (!treeProps?.expandedKeys && !treeProps?.defaultExpandAll) {
         const keys: React.Key[] = [];
         const collect = (nodes: T[]) => {
           nodes.forEach(node => {
             keys.push(rowKey(node));
             const children = node[childrenKey] as T[];
             if (children && children.length > 0) {
               collect(children);
             }
           });
         };
         collect(items || []);
         setExpandedKeys(keys);
      }

    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    reload: doFetch
  }));

  useEffect(() => {
    doFetch();
  }, []);

  const treeData = useMemo(() => {
    const convert = (items: T[]): DataNode[] => {
      return items.map(item => {
        const key = rowKey(item);
        const children = item[childrenKey] as T[];
        
        return {
          ...item, // Pass through other properties
          key,
          title: renderTitle ? renderTitle(item) : item.name || item.title,
          children: children && children.length > 0 ? convert(children) : undefined,
          isLeaf: !children || children.length === 0
        };
      });
    };
    return convert(data);
  }, [data, renderTitle, rowKey, childrenKey]);

  const onExpand = (keys: React.Key[]) => {
    setExpandedKeys(keys);
  };

  return (
    <Card
      title={title}
      bordered={false}
      extra={
        <Space>
          {showReload && (
            <Button icon={<ReloadOutlined />} onClick={doFetch} loading={loading}>
              刷新
            </Button>
          )}
          {extra}
        </Space>
      }
      {...cardProps}
    >
      <Spin spinning={loading}>
        {data.length > 0 ? (
          <Tree
            showLine
            blockNode
            selectable={false}
            expandedKeys={expandedKeys}
            onExpand={onExpand}
            treeData={treeData}
            {...treeProps}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    </Card>
  );
}) as <T>(props: TreeListProps<T> & { ref?: React.Ref<TreeListRef> }) => React.ReactElement;

export default TreeList;
